import React, { useMemo, useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Checkbox } from "@components/common/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/common/card"
import useTranslator from "@hooks/use-translator"
import axios from "axios"
import { SERVER_URL, USER_ROLE_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAuth } from "@stores/auth-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import PmsLoader from "@components/common/loader"
import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import { Progress } from "@components/common/progress"
import { toast } from "@hooks/use-toast"

const FormSchema = z.object({
  options: z.array(z.string()).refine((value) => value.some((item) => item), {
    message: "No changes detected",
  }),
})

interface Props {
  role?: DictType
}
const RolePermissions = ({ role }: Props) => {
  const [defaultOptions, setDefaultOptions] = useState([""])

  const { authConfig } = useAuth()

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id
  const userId = authConfig?.id

  const notBooleanFields = [
    "project_to_edit",
    "project_to_view",
    "tasks_to_edit",
    "teams_to_manage",
  ]

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      options: defaultOptions,
    },
  })

  const getUserRoles = async () => {
    const response = await axios.get(
      SERVER_URL +
        `${USER_ROLE_DICTIONARY_ENDPOINT}?context_type=organization%20user&context_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const {
    data: roles,
    isLoading,
    refetch,
    isFetched,
    error,
  } = useQuery({
    queryKey: ["roles", orgId],

    queryFn: getUserRoles,
  })

  const roleOptions = useMemo(() => {
    const opt = roles.filter((r: DictType) => r?.name === role?.name)[0]
    const filteredPermissions = Object.fromEntries(
      Object.entries(opt).filter(([key]) => key !== "manage_any_teams"),
    )

    delete filteredPermissions?.id
    delete filteredPermissions?.created_at
    delete filteredPermissions?.name
    delete filteredPermissions?.project_to_edit
    delete filteredPermissions?.project_to_view
    delete filteredPermissions?.teams_to_manage
    delete filteredPermissions?.tasks_to_edit

    //   Get defaults
    Object.keys(filteredPermissions)?.map((key) => {
      if (filteredPermissions[key] === true) {
        setDefaultOptions((prev) => [...prev, key])
      }
    })

    return filteredPermissions
  }, [isFetched])

  const handleCheckboxChange = (checked: boolean, field: DictType, name: string) => {
    let updatedOptions: string[]
    if (checked) {
      // If checked, add the option.name to the existing list
      updatedOptions = [...(defaultOptions ?? []), name]
    } else {
      // If unchecked, filter out the option.name
      updatedOptions = defaultOptions.filter((value: string) => value !== name) || []
    }

    setDefaultOptions([...new Set(updatedOptions)])
    return checked
      ? field.onChange([...new Set(updatedOptions)])
      : field.onChange([...new Set(updatedOptions)])
  }

  const setRolePermissions = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + USER_ROLE_DICTIONARY_ENDPOINT + `/edit`,
      {
        role_id: role?.id,
        user_id: userId,
        name: role?.name,
        ...payload,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }
  const updateRolePermissionsMutation = useMutation({
    mutationKey: ["update-role-permissions"],
    mutationFn: (payload: DictType) => setRolePermissions(payload),
    onSuccess: (data) => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("Role permissions updated"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to update role permissions"),
        variant: "error",
      })
    },
  })

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const selectedOptions = data?.options

    const payload: DictType = {}
    Object.keys(roleOptions)?.map((key) => {
      return (payload[String(key)] = selectedOptions.includes(key || "") ? true : false)
    })

    updateRolePermissionsMutation.mutate({ ...payload })
  }

  if (error) {
    const err = error as DictType

    if (err.response.status == 403) {
      return <></>
    }
  }

  return (
    <Card className='relative max-h-[60vh] min-w-[250px] overflow-hidden'>
      <CardHeader className='flex w-full flex-col items-start justify-start border-b py-4'>
        <CardTitle className='capitalize'>{translate(role?.name) ?? <Progress />}</CardTitle>
        <CardDescription>{translate("Customize role permissions")}</CardDescription>
      </CardHeader>

      <CardContent className='grid gap-4 p-6 text-sm'>
        {isLoading && <PmsLoader />}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
            <FormField
              control={form.control}
              name='options'
              render={() => (
                <FormItem>
                  <div className='grid grid-cols-1 gap-x-3 gap-y-5 sm:grid-cols-2 md:grid-cols-3 md:gap-y-8'>
                    {Object.keys(roleOptions)?.map((key: string, index) => (
                      <FormField
                        key={index}
                        control={form.control}
                        name='options'
                        render={({ field }) => {
                          return (
                            <FormItem
                              // key={roleOptions[key]}
                              className='flex flex-row items-start space-x-3 space-y-0'
                            >
                              <FormControl>
                                <button type='submit' className='flex items-center justify-start'>
                                  <Checkbox
                                    checked={defaultOptions?.includes(key || "")}
                                    className={cn(isHebrew && "mx-2")}
                                    onCheckedChange={(checked) =>
                                      handleCheckboxChange(!!checked, field, key || "")
                                    }
                                  />
                                </button>
                              </FormControl>
                              <FormLabel className='p-0 text-xs font-semibold capitalize tracking-wide'>
                                {translate(key?.replaceAll("_", " ") || "")}
                              </FormLabel>
                            </FormItem>
                          )
                        }}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

export default RolePermissions
